import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Currency from 'components/Form/Currency';
import Checkbox from 'components/Form/Checkbox';
import Textarea from 'components/Form/Textarea';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonRequired,
  CommonScrollIntoView,
} from 'types/props';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import { NonPaymentFederalOptionsValues, nonPaymentNonFederalActionsOptions, taxTypes } from 'constants/enums/financialOptions';
import { FinancialNonpaymentNonfederalItemModel } from 'forms/sections/financial/nonpaymentNonfederal/types';
import { formIncludesPvqPartC } from 'helpers/form';
import NotApplicable from 'components/Form/NotApplicable';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import { CheckboxModel } from 'forms/shared/types';

interface Props extends Partial<FinancialNonpaymentNonfederalItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  formType?: string
  scrollIntoView?: CommonScrollIntoView
}

export default class NonpaymentNonfederalItem extends ValidationElement<Props> {
  static defaultProps: Partial<Props> = {
    onUpdate: () => { },
    onError: () => { },
    required: false,
    formType: '',
    scrollIntoView: false,
  };

  update = (queue: Partial<FinancialNonpaymentNonfederalItemModel>) => {
    this.props.onUpdate({
      TaxType: this.props.TaxType,
      TaxTypeExplain: this.props.TaxTypeExplain,
      Year: this.props.Year,
      AmountOwed: this.props.AmountOwed,
      AmountOwedIdk: this.props.AmountOwedIdk,
      WhenPaidInFull: this.props.WhenPaidInFull,
      FilingDate: this.props.FilingDate,
      WhenFiled: this.props.WhenFiled,
      HasFiledOnTime: this.props.HasFiledOnTime,
      ActionsTaken: this.props.ActionsTaken,
      ActionsTakenReason: this.props.ActionsTakenReason,
      ...queue,
    });
  };

  updateWhenFiled = (values: FinancialNonpaymentNonfederalItemModel['WhenFiled']) => {
    this.update({
      WhenFiled: values,
    });
  };

  updateFilingDate = (values: FinancialNonpaymentNonfederalItemModel['FilingDate']) => {
    this.update({
      FilingDate: values,
    });
  };

  updateTaxType = (values: FinancialNonpaymentNonfederalItemModel['TaxType']) => {
    this.update({
      TaxType: values,
    });
  };

  updateWhenPaidInFull = (values: FinancialNonpaymentNonfederalItemModel['WhenPaidInFull']) => {
    this.update({
      WhenPaidInFull: values,
    });
  };

  updateTaxTypeExplain = (values: FinancialNonpaymentNonfederalItemModel['TaxTypeExplain']) => {
    this.update({
      TaxTypeExplain: values,
    });
  };

  updateYear = (values: FinancialNonpaymentNonfederalItemModel['Year']) => {
    this.update({
      Year: values,
    });
  };

  updateAmountOwed = (values: FinancialNonpaymentNonfederalItemModel['AmountOwed']) => {
    this.update({
      AmountOwed: values,
    });
  };

  updateAmountOwedIdk = (values: FinancialNonpaymentNonfederalItemModel['AmountOwedIdk']) => {
    this.update({
      AmountOwedIdk: values,
    });
  };

  updateActionsTaken = (values: CheckboxModel) => {
    const actionTaken = values?.value;
    let selected = [...((this.props.ActionsTaken || {}).values || [])];
    if (selected.includes(actionTaken)) {
      selected.splice(selected.indexOf(actionTaken), 1);
    } else {
      selected.push(actionTaken);
    }
    if (selected.includes('None')) {
      selected = ['None'];
    }

    this.update({
      ActionsTaken: { values: selected },
    });
  };

  updateActionsTakenReason = (values: FinancialNonpaymentNonfederalItemModel['ActionsTakenReason']) => {
    this.update({
      ActionsTakenReason: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    const { formType } = this.props;

    return (
      <div className="nonpaymentnonfederal-item">
        <Field
          title={i18n.t('financial.pvq.nonpaymentNonfederal.branch.ifYes.typeCombobox.taxType')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.TaxType`}
        >
          <RadioGroup
            {...this.props.TaxType}
            className="option-list option-list-vertical"
            selectedValue={(this.props.TaxType || {}).value}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.TaxTypeRadioGroup`}
          >
            {taxTypes.map((taxType) => (
              <Radio
                key={taxType}
                label={taxType}
                value={taxType}
                onUpdate={this.updateTaxType}
              />
            ))}
          </RadioGroup>
        </Field>

        {this.props.TaxType?.value === 'Other' && (
          <Field
            title={i18n.t('financial.pvq.nonpaymentNonfederal.branch.ifYes.typeCombobox.ifOther')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.TaxTypeExplain`}
          >
            <Textarea
              {...this.props.TaxTypeExplain}
              attribute={`${attribute}.TaxTypeExplain`}
              name="taxtypeexplain"
              onUpdate={this.updateTaxTypeExplain}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Field>
        )}

        <Field
          title={i18n.t('financial.pvq.nonpaymentNonfederal.branch.ifYes.whenFailDate')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Year`}
        >
          <DateControl
            {...this.props.Year}
            attribute={`${attribute}.Year`}
            name="year"
            onUpdate={this.updateYear}
            hideDay
            hideMonth
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            showEstimated={false}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.nonpaymentNonfederal.branch.ifYes.amountOwedTextIdk')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.AmountOwed`}
        >
          <NotApplicable
            name="amountowedidk"
            label={i18n.t('financial.pvq.shared.idk')}
            {...this.props.AmountOwedIdk}
            onUpdate={this.updateAmountOwedIdk}
            onError={this.props.onError}
            attribute={`${this.props.attribute}.AmountOwedIdk`}
          >
            <Currency
              {...this.props.AmountOwed}
              maxLength="10"
              attribute={`${attribute}.AmountOwed`}
              name="amountowed"
              onUpdate={this.updateAmountOwed}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('financial.pvq.nonpaymentNonfederal.branch.ifYes.whenFileDate')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.FilingDate`}
        >
          <DateControl
            {...this.props.FilingDate}
            attribute={`${attribute}.FilingDate`}
            name="filingDate"
            onUpdate={this.updateFilingDate}
            hideDay
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            showEstimated={false}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.nonpaymentNonfederal.branch.ifYes.actionsCombobox.actionsTaken')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.ActionsTaken`}
        >
          <CheckboxGroup
            attribute={`${attribute}.ActionsTaken`}
            className="option-list option-list-vertical"
            onError={this.props.onError}
            required={this.props.required}
            selectedValues={this.props.ActionsTaken?.values}
          >
            {nonPaymentNonFederalActionsOptions.map((act) => (
              <Checkbox
                key={act.key}
                label={act.text}
                value={act.value}
                onUpdate={this.updateActionsTaken}
                onError={this.props.onError}
                onBlur={this.props.onBlur}
                onFocus={this.props.onFocus}
              />
            ))}
          </CheckboxGroup>
        </Field>

        {(this.props.ActionsTaken?.values?.includes(NonPaymentFederalOptionsValues.other) || this.props.ActionsTaken?.values?.includes(NonPaymentFederalOptionsValues.none)) && (
          <Field
            title={i18n.t('financial.pvq.nonpaymentNonfederal.branch.ifYes.actionsCombobox.ifNoneOrOtherTextArea')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ActionsTakenReason`}
          >
            <Textarea
              {...this.props.ActionsTakenReason}
              attribute={`${attribute}.ActionsTakenReason`}
              scrollIntoView={this.props.scrollIntoView}
              onError={this.props.onError}
              onUpdate={this.updateActionsTakenReason}
              required={this.props.required}
            />
          </Field>
        )}

        {formIncludesPvqPartC(formType) && this.props.ActionsTaken?.values?.includes(NonPaymentFederalOptionsValues.paidInFull) && (
        <>
          <Field
            title={i18n.t('financial.pvq.nonpaymentNonfederal.branch.ifYes.pvqABC.branch.ifYes.resolved')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.WhenPaidInFull`}
          >
            <DateControl
              {...this.props.WhenPaidInFull}
              attribute={`${attribute}.WhenPaidInFull`}
              name="paidinfulldate"
              onUpdate={this.updateWhenPaidInFull}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              hideDay
              showEstimated
            />
          </Field>
          <Field
            title={i18n.t('financial.pvq.nonpaymentNonfederal.branch.ifYes.pvqABC.whenFiledEstimated')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.WhenFiled`}
          >
            <DateControl
              {...this.props.WhenFiled}
              attribute={`${attribute}.WhenFiled`}
              name="fileddate"
              onUpdate={this.updateWhenFiled}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              hideDay
              showEstimated
            />
          </Field>
        </>
        )}
      </div>
    );
  }
}
